import { useEffect, useMemo, useState } from 'react'
import { API } from './api'
import { adminRequest, type ShopCategory } from './EshopCategories'

export type AdminProduct = {
  id: string
  name: string
  category: string
  unit: string
  priceCents: number
  active: boolean
  plu?: string | null
  sku?: string | null
  allergens?: string | null
  glutenFree?: boolean
  lactoseFree?: boolean
  eshopVisible?: boolean
  vatRate?: number | null
  fiskalproVatRate?: number | null
  weightGrams?: number | null
  volumeMl?: number | null
  shelfLife?: string | null
  allowPickup?: boolean
  allowLocalDelivery?: boolean
  allowShipping?: boolean
  shopCategories?: Array<{ categoryId: string }>
  variants?: Array<{ id: string; priceCents: number; active: boolean }>
  images?: Array<{ id: string; url: string; isMain: boolean; isAdminMain?: boolean; visible?: boolean }>
  isBundle?: boolean
  bundleItems?: Array<{ id: string; componentProductId: string; quantity: number; component: { id: string; name: string; unit: string } }>
}

type Status = 'all' | 'eshop' | 'hidden' | 'inactive'
type Issue = 'noCategory' | 'noAllergens' | 'noVat' | 'noPhoto'
type Sort = 'name' | 'price-asc' | 'price-desc' | 'plu' | 'group'
type View = 'table' | 'grid'
type Filters = { search: string; group: string; shopCategory: string; includeSubcats: boolean; status: Status; issues: Issue[]; sort: Sort; view: View }

const DEFAULT_FILTERS: Filters = { search: '', group: '', shopCategory: '', includeSubcats: true, status: 'all', issues: [], sort: 'name', view: 'table' }
const STORAGE_KEY = 'emili-admin-product-filters'
const euro = (cents: number) => (cents / 100).toLocaleString('sk-SK', { style: 'currency', currency: 'EUR' })
const norm = (value: string) => value.toLocaleLowerCase('sk').normalize('NFD').replace(/\p{Diacritic}/gu, '')

const ISSUES: Array<{ id: Issue; label: string; title: string }> = [
  { id: 'noCategory', label: 'Bez kategórie', title: 'V e-shope sa nezobrazí – nie je v žiadnej kategórii' },
  { id: 'noAllergens', label: 'Bez alergénov', title: 'Zákazník nevidí alergény' },
  { id: 'noVat', label: 'Bez DPH', title: 'Nedá sa zaplatiť online ani vystaviť faktúru' },
  { id: 'noPhoto', label: 'Bez fotografie', title: 'Produkt nemá fotografiu' },
]

type DeliveryKey = 'allowPickup' | 'allowLocalDelivery' | 'allowShipping'
const DELIVERY: Array<{ key: DeliveryKey; icon: string; label: string }> = [
  { key: 'allowPickup', icon: '🏠', label: 'Osobný odber' },
  { key: 'allowLocalDelivery', icon: '🚗', label: 'Doručenie na adresu' },
  { key: 'allowShipping', icon: '📦', label: 'Kuriér – balík' },
]
const deliveryOn = (p: AdminProduct, key: DeliveryKey) => key === 'allowShipping' ? p.allowShipping === true : p[key] !== false

const inEshop = (p: AdminProduct) => p.active && p.eshopVisible !== false
const hasIssue = (p: AdminProduct, issue: Issue) => {
  if (issue === 'noPhoto') return !(p.images ?? []).length
  if (!inEshop(p)) return false
  if (issue === 'noCategory') return !(p.shopCategories ?? []).length
  if (issue === 'noAllergens') return !p.allergens
  return (p.vatRate ?? p.fiskalproVatRate) === null || (p.vatRate ?? p.fiskalproVatRate) === undefined
}
const priceOf = (p: AdminProduct) => {
  const active = (p.variants ?? []).filter(v => v.active)
  return active.length ? Math.min(...active.map(v => v.priceCents)) : p.priceCents
}

/** Pred otvorením stránky nastaví filter (napr. z Prehľadu „Produkty bez kategórie“). */
export function presetProductFilters(patch: { issues?: Issue[] }) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readFilters(), search: '', group: '', shopCategory: '', status: 'all', ...patch })) } catch { /* nevadí */ }
}

function readFilters(): Filters {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null')
    if (saved && typeof saved === 'object') return { ...DEFAULT_FILTERS, ...saved, search: '' }
  } catch { /* úložisko nie je dostupné */ }
  return DEFAULT_FILTERS
}

export default function ProductsAdmin({ products, loading, error, shopCategories, groupLabel, onEdit, onReload, onOpenGroups }: {
  products: AdminProduct[]
  loading?: boolean
  error?: string
  shopCategories: ShopCategory[]
  groupLabel: (key: string) => string
  onEdit: (product: AdminProduct) => void
  onReload: () => void | Promise<void>
  onOpenGroups: () => void
}) {
  const [filters, setFilters] = useState<Filters>(readFilters)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [actionError, setActionError] = useState('')

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...filters, search: '' })) } catch { /* nevadí */ }
  }, [filters])
  const update = (patch: Partial<Filters>) => setFilters(current => ({ ...current, ...patch }))

  const categoryById = useMemo(() => new Map(shopCategories.map(c => [c.id, c])), [shopCategories])
  const categoryLabel = (id: string) => {
    const category = categoryById.get(id)
    if (!category) return null
    const parent = category.parentId ? categoryById.get(category.parentId) : undefined
    return parent ? `${parent.name} › ${category.name}` : category.name
  }
  const groups = useMemo(() => [...new Set(products.map(p => p.category))]
    .sort((a, b) => groupLabel(a).localeCompare(groupLabel(b), 'sk', { numeric: true })), [products, groupLabel])

  // Počty pre záložky stavu a „treba doplniť“ (počítané z ostatných filtrov).
  const base = useMemo(() => {
    const q = norm(filters.search.trim())
    return products.filter(p =>
      (!q || norm(`${p.name} ${p.plu ?? ''} ${p.sku ?? ''}`).includes(q))
      && (!filters.group || p.category === filters.group)
      && (!filters.shopCategory || (filters.shopCategory === '__none__'
        ? !(p.shopCategories ?? []).length
        : (p.shopCategories ?? []).some(link => link.categoryId === filters.shopCategory || (filters.includeSubcats && categoryById.get(link.categoryId)?.parentId === filters.shopCategory)))))
  }, [products, filters.search, filters.group, filters.shopCategory, filters.includeSubcats, categoryById])

  const statusCount: Record<Status, number> = {
    all: base.length,
    eshop: base.filter(inEshop).length,
    hidden: base.filter(p => p.active && p.eshopVisible === false).length,
    inactive: base.filter(p => !p.active).length,
  }
  const byStatus = base.filter(p => filters.status === 'all' ? true : filters.status === 'eshop' ? inEshop(p) : filters.status === 'hidden' ? p.active && p.eshopVisible === false : !p.active)
  const issueCount = Object.fromEntries(ISSUES.map(i => [i.id, byStatus.filter(p => hasIssue(p, i.id)).length])) as Record<Issue, number>

  const collator = (a: string, b: string) => a.localeCompare(b, 'sk', { numeric: true, sensitivity: 'base' })
  const visible = byStatus.filter(p => filters.issues.every(issue => hasIssue(p, issue))).sort((a, b) => {
    switch (filters.sort) {
      case 'price-asc': return priceOf(a) - priceOf(b) || collator(a.name, b.name)
      case 'price-desc': return priceOf(b) - priceOf(a) || collator(a.name, b.name)
      case 'plu': return collator(a.plu ?? '~', b.plu ?? '~') || collator(a.name, b.name)
      case 'group': return collator(groupLabel(a.category), groupLabel(b.category)) || collator(a.name, b.name)
      default: return collator(a.name, b.name)
    }
  })

  const activeFilterCount = [filters.search, filters.group, filters.shopCategory].filter(Boolean).length + (filters.status !== 'all' ? 1 : 0) + filters.issues.length
  const clearFilters = () => update({ search: '', group: '', shopCategory: '', status: 'all', issues: [] })

  const visibleIds = visible.map(p => p.id)
  const selectedVisible = visibleIds.filter(id => selected.has(id))
  const allSelected = visibleIds.length > 0 && selectedVisible.length === visibleIds.length
  const toggleAll = () => setSelected(allSelected ? new Set([...selected].filter(id => !visibleIds.includes(id))) : new Set([...selected, ...visibleIds]))
  const toggleOne = (id: string) => setSelected(current => { const next = new Set(current); if (next.has(id)) next.delete(id); else next.add(id); return next })

  async function setEshopVisible(ids: string[], eshopVisible: boolean) {
    if (!ids.length) return
    setBusy(true); setActionError(''); setMessage('')
    try {
      const data = await adminRequest('/api/admin/products/eshop-visibility', { method: 'PATCH', body: JSON.stringify({ productIds: ids, eshopVisible }) })
      await onReload()
      const n = Number(data?.updated ?? ids.length)
      const word = n === 1 ? ['produkt', 'je', eshopVisible ? 'zobrazený' : 'skrytý'] : n >= 2 && n <= 4 ? ['produkty', 'sú', eshopVisible ? 'zobrazené' : 'skryté'] : ['produktov', 'je', eshopVisible ? 'zobrazených' : 'skrytých']
      setMessage(`${n} ${word[0]} ${word[1]} ${word[2]} ${eshopVisible ? 'v e-shope' : 'z e-shopu'}.`)
      if (ids.length > 1) setSelected(new Set())
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Zmena sa nepodarila.')
    } finally { setBusy(false) }
  }

  async function setDelivery(ids: string[], key: DeliveryKey, value: boolean) {
    if (!ids.length) return
    setBusy(true); setActionError(''); setMessage('')
    try {
      const data = await adminRequest('/api/admin/products/delivery-methods', { method: 'PATCH', body: JSON.stringify({ productIds: ids, [key]: value }) })
      await onReload()
      const option = DELIVERY.find(d => d.key === key)!
      setMessage(`${option.label}: ${value ? 'povolené' : 'zakázané'} pri ${Number(data?.updated ?? ids.length)} produktoch.`)
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Zmena sa nepodarila.')
    } finally { setBusy(false) }
  }

  const mainImage = (p: AdminProduct) => (p.images ?? []).find(i => i.isAdminMain) ?? (p.images ?? []).find(i => i.isMain) ?? (p.images ?? [])[0]
  const categoryChips = (p: AdminProduct) => (p.shopCategories ?? []).map(link => categoryLabel(link.categoryId)).filter((x): x is string => Boolean(x))
  const issuesOf = (p: AdminProduct) => ISSUES.filter(i => hasIssue(p, i.id))

  return <div className="panel products-admin">
    <div className="pa-head">
      <div>
        <h2>Zoznam produktov</h2>
        <p>{products.length} produktov · {statusCount.eshop} v e-shope</p>
      </div>
      <div className="pa-head-actions">
        <button type="button" className="pa-ghost" onClick={onOpenGroups}>Skupiny z pokladne</button>
        <button type="button" className="pa-ghost" onClick={() => void onReload()} disabled={loading}>↻ Obnoviť</button>
      </div>
    </div>

    <div className="pa-toolbar">
      <label className="pa-search">
        <span aria-hidden="true">⌕</span>
        <input placeholder="Hľadať názov, PLU alebo SKU…" value={filters.search} onChange={e => update({ search: e.target.value })} />
        {filters.search && <button type="button" aria-label="Vymazať hľadanie" onClick={() => update({ search: '' })}>×</button>}
      </label>
      <select aria-label="Skupina z pokladne" className={filters.group ? 'is-set' : ''} value={filters.group} onChange={e => update({ group: e.target.value })}>
        <option value="">Všetky skupiny</option>
        {groups.map(g => <option key={g} value={g}>{groupLabel(g)}</option>)}
      </select>
      <div className="pa-shopcat-group">
        <select aria-label="Kategória e-shopu" className={filters.shopCategory ? 'is-set' : ''} value={filters.shopCategory} onChange={e => update({ shopCategory: e.target.value })}>
          <option value="">Všetky kategórie</option>
          {shopCategories.map(c => <option key={c.id} value={c.id}>{c.parentId ? `  › ${c.name}` : c.name}</option>)}
          <option value="__none__">Bez kategórie e-shopu</option>
        </select>
        {filters.shopCategory && !categoryById.get(filters.shopCategory)?.parentId && <label className="pa-subs-toggle"><input type="checkbox" checked={filters.includeSubcats} onChange={e => update({ includeSubcats: e.target.checked })} /> Vrátane podkategórií</label>}
      </div>
      <select aria-label="Zoradenie" value={filters.sort} onChange={e => update({ sort: e.target.value as Sort })}>
        <option value="name">Názov A → Z</option>
        <option value="price-asc">Cena od najnižšej</option>
        <option value="price-desc">Cena od najvyššej</option>
        <option value="plu">PLU</option>
        <option value="group">Skupina z pokladne</option>
      </select>
      <div className="pa-view" role="group" aria-label="Zobrazenie">
        <button type="button" className={filters.view === 'table' ? 'is-on' : ''} aria-pressed={filters.view === 'table'} title="Zoznam" onClick={() => update({ view: 'table' })}>☰</button>
        <button type="button" className={filters.view === 'grid' ? 'is-on' : ''} aria-pressed={filters.view === 'grid'} title="Kartičky" onClick={() => update({ view: 'grid' })}>▦</button>
      </div>
    </div>

    <div className="pa-filters-row">
      <div className="pa-tabs" role="tablist" aria-label="Stav produktu">
        {([['all', 'Všetky'], ['eshop', 'V e-shope'], ['hidden', 'Mimo e-shopu'], ['inactive', 'Neaktívne']] as Array<[Status, string]>).map(([id, label]) =>
          <button key={id} type="button" role="tab" aria-selected={filters.status === id} className={filters.status === id ? 'is-on' : ''} onClick={() => update({ status: id })}>
            {label}<span>{statusCount[id]}</span>
          </button>)}
      </div>
      <div className="pa-issues" aria-label="Treba doplniť">
        <span className="pa-issues-label">Treba doplniť:</span>
        {ISSUES.map(issue => {
          const on = filters.issues.includes(issue.id)
          return <button key={issue.id} type="button" title={issue.title} aria-pressed={on} className={`pa-chip ${on ? 'is-on' : ''}`} disabled={!on && !issueCount[issue.id]}
            onClick={() => update({ issues: on ? filters.issues.filter(i => i !== issue.id) : [...filters.issues, issue.id] })}>
            {issue.label}<span>{issueCount[issue.id]}</span>
          </button>
        })}
      </div>
    </div>

    <div className="pa-resultbar">
      <span>{visible.length === products.length ? `${visible.length} produktov` : `Zobrazených ${visible.length} z ${products.length}`}</span>
      {activeFilterCount > 0 && <button type="button" className="pa-link" onClick={clearFilters}>Zrušiť filtre ({activeFilterCount})</button>}
    </div>

    {(error || actionError) && <p className="error-message">{error || actionError}</p>}
    {message && <p className="pa-message" role="status">{message}</p>}

    {selectedVisible.length > 0 && <div className="pa-bulk" role="region" aria-label="Hromadné úpravy">
      <strong>Vybrané: {selectedVisible.length}</strong>
      <button type="button" disabled={busy} onClick={() => void setEshopVisible(selectedVisible, true)}>Zobraziť v e-shope</button>
      <button type="button" disabled={busy} onClick={() => void setEshopVisible(selectedVisible, false)}>Skryť z e-shopu</button>
      <select className="pa-bulk-select" aria-label="Nastaviť spôsob doručenia" value="" disabled={busy} onChange={e => { const [key, value] = e.target.value.split(':'); if (key) void setDelivery(selectedVisible, key as DeliveryKey, value === '1') }}>
        <option value="">Doručenie…</option>
        {DELIVERY.flatMap(d => [<option key={`${d.key}1`} value={`${d.key}:1`}>{d.icon} Povoliť: {d.label}</option>, <option key={`${d.key}0`} value={`${d.key}:0`}>{d.icon} Zakázať: {d.label}</option>])}
      </select>
      <button type="button" className="pa-link" onClick={() => setSelected(new Set())}>Zrušiť výber</button>
    </div>}

    {loading && !products.length ? <p className="empty-message">Načítavam produkty…</p>
      : !visible.length ? <div className="pa-empty">
        <span>♡</span>
        <strong>{products.length ? 'Žiadny produkt nevyhovuje filtrom' : 'Zatiaľ nemáš žiadne produkty'}</strong>
        {activeFilterCount > 0 && <button type="button" className="pa-link" onClick={clearFilters}>Zrušiť filtre</button>}
      </div>
      : filters.view === 'table' ? <div className="pa-table-wrap"><table className="pa-table">
        <thead><tr>
          <th className="pa-col-check"><input type="checkbox" aria-label="Vybrať všetky zobrazené" checked={allSelected} onChange={toggleAll} /></th>
          <th>Produkt</th>
          <th>Skupina z pokladne</th>
          <th>Kategórie e-shopu</th>
          <th className="pa-col-delivery">Doručenie</th>
          <th className="pa-col-price">Cena</th>
          <th className="pa-col-eshop">V e-shope</th>
        </tr></thead>
        <tbody>{visible.map(p => {
          const image = mainImage(p)
          const chips = categoryChips(p)
          const issues = issuesOf(p)
          const variants = (p.variants ?? []).filter(v => v.active).length
          return <tr key={p.id} className={`${selected.has(p.id) ? 'is-selected' : ''} ${p.active ? '' : 'is-inactive'}`} onClick={() => onEdit(p)}>
            <td className="pa-col-check" onClick={e => e.stopPropagation()}><input type="checkbox" aria-label={`Vybrať ${p.name}`} checked={selected.has(p.id)} onChange={() => toggleOne(p.id)} /></td>
            <td>
              <div className="pa-product">
                <span className="pa-thumb">{image ? <img src={`${API}${image.url}`} alt="" loading="lazy" /> : <span>♡</span>}</span>
                <span className="pa-name">
                  <strong>{p.name}{p.isBundle && <span className="pa-bundle-badge" title="Komplet – skladá sa z viacerých produktov">Komplet</span>}{p.glutenFree && <span className="pa-diet-badge" title="Bezlepkové">🌾</span>}{p.lactoseFree && <span className="pa-diet-badge" title="Bezlaktózové">🥛</span>}</strong>
                  <small>{[p.plu ? `PLU ${p.plu}` : null, variants ? `${variants} ${variants === 1 ? 'variant' : variants < 5 ? 'varianty' : 'variantov'}` : null, !p.active ? 'neaktívny' : null].filter(Boolean).join(' · ') || '—'}</small>
                  {issues.length > 0 && <span className="pa-issues-inline">{issues.map(i => <span key={i.id} className="pa-issue" title={i.title}>{i.label}</span>)}</span>}
                </span>
              </div>
            </td>
            <td className="pa-muted">{groupLabel(p.category)}</td>
            <td>{chips.length ? <span className="pa-cats">{chips.slice(0, 2).map(c => <span key={c} className="pa-cat">{c}</span>)}{chips.length > 2 && <span className="pa-cat is-more" title={chips.slice(2).join(', ')}>+{chips.length - 2}</span>}</span> : <span className="pa-muted">—</span>}</td>
            <td className="pa-col-delivery"><span className="pa-delivery">{DELIVERY.map(d => <span key={d.key} className={deliveryOn(p, d.key) ? 'is-on' : ''} title={`${d.label}: ${deliveryOn(p, d.key) ? 'áno' : 'nie'}`}>{d.icon}</span>)}</span></td>
            <td className="pa-col-price"><strong>{variants ? <small>od </small> : null}{euro(priceOf(p))}</strong><small> / {p.unit}</small></td>
            <td className="pa-col-eshop" onClick={e => e.stopPropagation()}>
              <button type="button" role="switch" aria-checked={p.eshopVisible !== false} aria-label={`${p.name} v e-shope`} className={`pa-switch ${p.eshopVisible !== false ? 'is-on' : ''}`} disabled={busy || !p.active}
                title={!p.active ? 'Neaktívny produkt sa v e-shope nezobrazuje' : p.eshopVisible !== false ? 'Skryť z e-shopu' : 'Zobraziť v e-shope'}
                onClick={() => void setEshopVisible([p.id], p.eshopVisible === false)}><span /></button>
            </td>
          </tr>
        })}</tbody>
      </table></div>
      : <div className="pa-grid">{visible.map(p => {
        const image = mainImage(p)
        const issues = issuesOf(p)
        return <article key={p.id} className={`pa-card ${p.active ? '' : 'is-inactive'}`} onClick={() => onEdit(p)}>
          <div className="pa-card-photo">
            {image ? <img src={`${API}${image.url}`} alt="" loading="lazy" /> : <span>♡</span>}
            <label className="pa-card-check" onClick={e => e.stopPropagation()}><input type="checkbox" aria-label={`Vybrať ${p.name}`} checked={selected.has(p.id)} onChange={() => toggleOne(p.id)} /></label>
            <span className={`pa-card-state ${inEshop(p) ? 'is-on' : ''}`}>{!p.active ? 'Neaktívny' : p.eshopVisible === false ? 'Mimo e-shopu' : 'V e-shope'}</span>
          </div>
          <div className="pa-card-body">
            <strong>{p.name}{p.isBundle && <span className="pa-bundle-badge" title="Komplet – skladá sa z viacerých produktov">Komplet</span>}{p.glutenFree && <span className="pa-diet-badge" title="Bezlepkové">🌾</span>}{p.lactoseFree && <span className="pa-diet-badge" title="Bezlaktózové">🥛</span>}</strong>
            <small>{groupLabel(p.category)}{p.plu ? ` · PLU ${p.plu}` : ''}</small>
            <div className="pa-card-foot">
              <b>{(p.variants ?? []).some(v => v.active) ? 'od ' : ''}{euro(priceOf(p))}</b>
              {issues.length > 0 && <span className="pa-issue" title={issues.map(i => i.label).join(', ')}>{issues.length} {issues.length === 1 ? 'upozornenie' : 'upozornenia'}</span>}
            </div>
          </div>
        </article>
      })}</div>}
  </div>
}
